// src/components/project/EditorApp.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Toggle from "react-toggle";
import toast from "react-hot-toast";
import { ProjectDetailContent } from "./ProjectDetail";
import Config from "../../config/Config";
import CloseIcon from "@mui/icons-material/Close";
import PeopleIcon from "@mui/icons-material/People";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ImageIcon from "@mui/icons-material/Image";
import ImageUpload from "../common/ImageUpload";
import CodeMirror from "@uiw/react-codemirror";
import { markdown, markdownLanguage } from "@codemirror/lang-markdown";
import { languages } from "@codemirror/language-data";
import { EditorView, keymap } from "@codemirror/view";
import { indentUnit } from "@codemirror/language";
import { EditorState } from "@codemirror/state";
import { closeBrackets } from "@codemirror/autocomplete";

import { createTheme } from "@uiw/codemirror-themes";
import { tags as t } from "@lezer/highlight";

const EDITOR_THEMES = {
  midnight: {
    label: "Midnight",
    swatch: "#000000",
    theme: createTheme({
      theme: "dark",
      settings: {
        background: "#000000e6", foreground: "#c9d1d9", caret: "#59c6c1",
        selection: "#59c6c133", selectionMatch: "#59c6c122", lineHighlight: "#ffffff08",
        gutterBackground: "transparent", gutterForeground: "#475569", gutterBorder: "transparent",
      },
      styles: [
        { tag: t.heading1, color: "#59c6c1", fontWeight: "700", fontSize: "1.3em" },
        { tag: t.heading2, color: "#59c6c1", fontWeight: "600", fontSize: "1.15em" },
        { tag: [t.heading3, t.heading4, t.heading5, t.heading6], color: "#59c6c1", fontWeight: "600" },
        { tag: t.strong, color: "#e2e8f0", fontWeight: "700" },
        { tag: t.emphasis, color: "#c4b5fd", fontStyle: "italic" },
        { tag: [t.monospace, t.content], color: "#c9d1d9" },
        { tag: t.url, color: "#7dd3fc", textDecoration: "underline" },
        { tag: t.link, color: "#7dd3fc" },
        { tag: t.labelName, color: "#7dd3fc" },
        { tag: [t.processingInstruction, t.inserted], color: "#f0abfc" },
        { tag: t.strikethrough, textDecoration: "line-through", color: "#64748b" },
        { tag: t.quote, color: "#94a3b8", fontStyle: "italic" },
        { tag: t.list, color: "#fbbf24" },
        { tag: t.contentSeparator, color: "#334155" },
        { tag: [t.typeName, t.attributeName], color: "#7dd3fc" },
        { tag: [t.keyword], color: "#f472b6" },
        { tag: [t.string], color: "#a5f3fc" },
        { tag: [t.comment], color: "#64748b" },
        { tag: [t.number], color: "#fbbf24" },
        { tag: [t.meta], color: "#64748b" },
      ],
    }),
  },
  ocean: {
    label: "Ocean",
    swatch: "#1a1b2e",
    theme: createTheme({
      theme: "dark",
      settings: {
        background: "#1a1b2e", foreground: "#a6accd", caret: "#82aaff",
        selection: "#82aaff22", selectionMatch: "#82aaff15", lineHighlight: "#ffffff06",
        gutterBackground: "transparent", gutterForeground: "#4e5579", gutterBorder: "transparent",
      },
      styles: [
        { tag: t.heading1, color: "#82aaff", fontWeight: "700", fontSize: "1.3em" },
        { tag: t.heading2, color: "#82aaff", fontWeight: "600", fontSize: "1.15em" },
        { tag: [t.heading3, t.heading4, t.heading5, t.heading6], color: "#82aaff", fontWeight: "600" },
        { tag: t.strong, color: "#eeffff", fontWeight: "700" },
        { tag: t.emphasis, color: "#c792ea", fontStyle: "italic" },
        { tag: [t.monospace, t.content], color: "#a6accd" },
        { tag: t.url, color: "#89ddff", textDecoration: "underline" },
        { tag: t.link, color: "#89ddff" },
        { tag: t.labelName, color: "#89ddff" },
        { tag: [t.processingInstruction, t.inserted], color: "#c792ea" },
        { tag: t.strikethrough, textDecoration: "line-through", color: "#4e5579" },
        { tag: t.quote, color: "#676e95", fontStyle: "italic" },
        { tag: t.list, color: "#ffcb6b" },
        { tag: t.contentSeparator, color: "#2b2c45" },
        { tag: [t.typeName, t.attributeName], color: "#89ddff" },
        { tag: [t.keyword], color: "#c792ea" },
        { tag: [t.string], color: "#c3e88d" },
        { tag: [t.comment], color: "#4e5579" },
        { tag: [t.number], color: "#f78c6c" },
        { tag: [t.meta], color: "#4e5579" },
      ],
    }),
  },
  ember: {
    label: "Ember",
    swatch: "#1c1210",
    theme: createTheme({
      theme: "dark",
      settings: {
        background: "#1c1210", foreground: "#d4b8a0", caret: "#ff9e64",
        selection: "#ff9e6422", selectionMatch: "#ff9e6415", lineHighlight: "#ffffff06",
        gutterBackground: "transparent", gutterForeground: "#5c4033", gutterBorder: "transparent",
      },
      styles: [
        { tag: t.heading1, color: "#ff9e64", fontWeight: "700", fontSize: "1.3em" },
        { tag: t.heading2, color: "#ff9e64", fontWeight: "600", fontSize: "1.15em" },
        { tag: [t.heading3, t.heading4, t.heading5, t.heading6], color: "#ff9e64", fontWeight: "600" },
        { tag: t.strong, color: "#f0e0d0", fontWeight: "700" },
        { tag: t.emphasis, color: "#f7768e", fontStyle: "italic" },
        { tag: [t.monospace, t.content], color: "#d4b8a0" },
        { tag: t.url, color: "#73daca", textDecoration: "underline" },
        { tag: t.link, color: "#73daca" },
        { tag: t.labelName, color: "#73daca" },
        { tag: [t.processingInstruction, t.inserted], color: "#bb9af7" },
        { tag: t.strikethrough, textDecoration: "line-through", color: "#5c4033" },
        { tag: t.quote, color: "#8a6550", fontStyle: "italic" },
        { tag: t.list, color: "#e0af68" },
        { tag: t.contentSeparator, color: "#2e1e18" },
        { tag: [t.typeName, t.attributeName], color: "#73daca" },
        { tag: [t.keyword], color: "#bb9af7" },
        { tag: [t.string], color: "#9ece6a" },
        { tag: [t.comment], color: "#5c4033" },
        { tag: [t.number], color: "#ff9e64" },
        { tag: [t.meta], color: "#5c4033" },
      ],
    }),
  },
  paper: {
    label: "Paper",
    swatch: "#fafaf9",
    theme: createTheme({
      theme: "light",
      settings: {
        background: "#fafaf9", foreground: "#3c3836", caret: "#076678",
        selection: "#07667822", selectionMatch: "#07667815", lineHighlight: "#00000006",
        gutterBackground: "transparent", gutterForeground: "#a89984", gutterBorder: "transparent",
      },
      styles: [
        { tag: t.heading1, color: "#076678", fontWeight: "700", fontSize: "1.3em" },
        { tag: t.heading2, color: "#076678", fontWeight: "600", fontSize: "1.15em" },
        { tag: [t.heading3, t.heading4, t.heading5, t.heading6], color: "#076678", fontWeight: "600" },
        { tag: t.strong, color: "#1d2021", fontWeight: "700" },
        { tag: t.emphasis, color: "#8f3f71", fontStyle: "italic" },
        { tag: [t.monospace, t.content], color: "#3c3836" },
        { tag: t.url, color: "#076678", textDecoration: "underline" },
        { tag: t.link, color: "#076678" },
        { tag: t.labelName, color: "#076678" },
        { tag: [t.processingInstruction, t.inserted], color: "#8f3f71" },
        { tag: t.strikethrough, textDecoration: "line-through", color: "#a89984" },
        { tag: t.quote, color: "#7c6f64", fontStyle: "italic" },
        { tag: t.list, color: "#b57614" },
        { tag: t.contentSeparator, color: "#d5c4a1" },
        { tag: [t.typeName, t.attributeName], color: "#076678" },
        { tag: [t.keyword], color: "#9d0006" },
        { tag: [t.string], color: "#79740e" },
        { tag: [t.comment], color: "#a89984" },
        { tag: [t.number], color: "#8f3f71" },
        { tag: [t.meta], color: "#a89984" },
      ],
    }),
  },
};

const THEME_STORAGE_KEY = "i7_editor_theme";

const cmBaseExtensions = [
  markdown({ base: markdownLanguage, codeLanguages: languages }),
  EditorView.lineWrapping,
  closeBrackets(),
  indentUnit.of("    "),
  EditorState.tabSize.of(4),
];

export default function EditorApp({
  draftKey,
  initialData = {},
  initialContributors = [],
  onSave,
  onSuccess,
  onCancel,
  onDelete,
  projectId,
  showPublishToggle = true,
  minSplitPct = 25,
  maxSplitPct = 75,
  mobileBreakpointPx = 768,
}) {
  // Detect dark mode from localStorage (same as DarkModeToggle)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('i7-dark-mode');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Listen for theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark-theme'));
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);
  // ---- Contributors (owner M2M) ----
  const [contributors, setContributors] = useState(initialContributors);
  const [contribSearch, setContribSearch] = useState("");
  const [contribResults, setContribResults] = useState([]);
  const [contribSearching, setContribSearching] = useState(false);
  const contribSearchTimeout = useRef(null);

  useEffect(() => {
    if (initialContributors.length > 0) setContributors(initialContributors);
  }, [initialContributors]);

  const searchUsers = useCallback((query) => {
    if (contribSearchTimeout.current) clearTimeout(contribSearchTimeout.current);
    if (!query.trim()) {
      setContribResults([]);
      return;
    }
    contribSearchTimeout.current = setTimeout(async () => {
      setContribSearching(true);
      try {
        const res = await axios.get(`${Config().userUrl}?search=${encodeURIComponent(query.trim())}`);
        const users = res.data?.results || res.data || [];
        const existingIds = new Set(contributors.map((c) => c.id || c.pk));
        setContribResults(users.filter((u) => !existingIds.has(u.id) && !existingIds.has(u.pk)));
      } catch (err) {
        console.error("User search error:", err);
        setContribResults([]);
      } finally {
        setContribSearching(false);
      }
    }, 300);
  }, [contributors]);

  const addContributor = useCallback((user) => {
    setContributors((prev) => {
      if (prev.some((c) => (c.id || c.pk) === (user.id || user.pk))) return prev;
      return [...prev, user];
    });
    setContribSearch("");
    setContribResults([]);
  }, []);

  const removeContributor = useCallback((userId) => {
    setContributors((prev) => prev.filter((c) => (c.id || c.pk) !== userId));
  }, []);

  const [state, setState] = useState(() => ({
    title: "",
    subtitle: "",
    body: "",
    technology: "",
    skill: "",
    publish: false,
    saving: false,
    apiError: null,
    errors: {},
    ...initialData,
  }));

  // --- responsive: compute after mount (no window during init) ---
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () =>
      setIsMobile(typeof window !== "undefined" && window.innerWidth < mobileBreakpointPx);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [mobileBreakpointPx]);

  const [showJsonImport, setShowJsonImport] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [showGuide, setShowGuide] = useState(false);
  const [showContributors, setShowContributors] = useState(false);
  const [showBannerUpload, setShowBannerUpload] = useState(false);
  const [editorThemeKey, setEditorThemeKey] = useState(() =>
    localStorage.getItem(THEME_STORAGE_KEY) || "midnight"
  );
  const activeTheme = EDITOR_THEMES[editorThemeKey]?.theme || EDITOR_THEMES.midnight.theme;

  const cycleTheme = useCallback(() => {
    const keys = Object.keys(EDITOR_THEMES);
    const idx = keys.indexOf(editorThemeKey);
    const next = keys[(idx + 1) % keys.length];
    setEditorThemeKey(next);
    localStorage.setItem(THEME_STORAGE_KEY, next);
  }, [editorThemeKey]);

  const [split, setSplit] = useState(50);
  const dragging = useRef(false);
  const draftTimeout = useRef(null);

  const inputStyle = useMemo(() => ({
    backgroundColor: "#000000e6",
    color: "#59c6c1",
    borderColor: "#334155",
  }), []);

  // ---- Draft: restore (only on create — edit page gets fresh data from API) ----
  useEffect(() => {
    if (!draftKey || projectId) return; // skip draft restore on edit pages
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        const { picture_link, ...restored } = parsed;
        setState((s) => ({ ...s, ...restored, errors: {}, apiError: null }));
      }
    } catch (e) {
      console.warn("Draft restore failed", e);
    }
  }, [draftKey, projectId]);

  // ---- Draft: save (debounced + guarded) ----
  const queueDraftSave = useCallback((nextState) => {
    if (!draftKey) return;
    if (draftTimeout.current) clearTimeout(draftTimeout.current);
    draftTimeout.current = setTimeout(() => {
      try {
        const { errors, apiError, saving, ...clean } = nextState;
        localStorage.setItem(draftKey, JSON.stringify(clean));
      } catch (e) {
        console.warn("Draft save failed", e);
      }
    }, 350);
  }, [draftKey]);

  const setField = useCallback((id, value) => {
    setState((s) => {
      const next = { ...s, [id]: value };
      queueDraftSave(next);
      return next;
    });
  }, [queueDraftSave]);

  const handleChange = (e) => setField(e.target.id, e.target.value);
  const togglePublish = () => setField("publish", !state.publish);

  const clearDraftAndRefresh = () => {
    try { if (draftKey) localStorage.removeItem(draftKey); } catch {}
    if (typeof window !== "undefined") window.location.reload();
  };

  // ---- Validation ----
  const validators = {
    required: (label, v) => (!v?.trim() ? `${label} is required.` : null),
    max: (label, v, n) => (v && v.length > n ? `${label} must be ≤ ${n} characters.` : null),
  };

  const validate = useCallback((s) => {
    const errs = {};
    const add = (k, msg) => msg && (errs[k] = msg);
    add("title", validators.required("Title", s.title) || validators.max("Title", s.title, 150));
    add("subtitle", validators.max("Subtitle", s.subtitle, 150));
    add("technology", validators.max("Technology", s.technology, 150));
    add("skill", validators.max("Skill", s.skill, 150));
    add("body", validators.required("Body", s.body));
    return errs;
  }, []);

  const errors = useMemo(() => validate(state), [state, validate]);
  const hasErrors = Object.keys(errors).length > 0;

  // ---- Refs for latest state (used by save to avoid stale closures) ----
  const stateRef = useRef(state);
  const contributorsRef = useRef(contributors);
  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { contributorsRef.current = contributors; }, [contributors]);

  // ---- Save (defensive, reads from refs for latest values) ----
  const save = useCallback(async () => {
    if (typeof onSave !== "function") {
      console.error("EditorApp: onSave is not provided");
      toast.error("Save handler not configured");
      return;
    }
    const s = stateRef.current;
    const contribs = contributorsRef.current;
    setState((prev) => ({ ...prev, saving: true, apiError: null }));
    try {
      const payload = {
        title: s.title,
        subtitle: s.subtitle,
        body: s.body,
        technology: s.technology,
        skill: s.skill,
        publish: s.publish,
        owner: contribs.map((c) => c.id || c.pk),
      };
      const result = await onSave(payload);
      try { if (draftKey) localStorage.removeItem(draftKey); } catch {}
      toast.success("Project saved");
      setState((prev) => ({ ...prev, saving: false }));
      const returnedId = result?.id ?? result?.pk;
      if (onSuccess) onSuccess(returnedId);
    } catch (err) {
      console.error("Project Save Failed", err);
      console.error("Project Save Error Response:", err.response);
      console.error("Project Save Error Status:", err.response?.status);
      console.error("Project Save Error Data:", err.response?.data);

      const status = err.response?.status;
      const raw = err?.response?.data;

      const extractMessage = (data) => {
        if (!data) return "";
        if (typeof data === "string") return data;
        if (typeof data !== "object") return String(data);
        if (data.detail) return typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
        if (data.error) return typeof data.error === "string" ? data.error : JSON.stringify(data.error);
        const lines = [];
        for (const [k, v] of Object.entries(data)) {
          const msg = Array.isArray(v) ? v.join(" ") : String(v);
          lines.push(`${k}: ${msg}`);
        }
        return lines.join("\n");
      };

      const serverMsg = extractMessage(raw);

      const serverErrors = (raw && typeof raw === "object" && !Array.isArray(raw))
        ? Object.fromEntries(
            Object.entries(raw)
              .filter(([k]) => !["detail", "error", "non_field_errors"].includes(k))
              .map(([k, v]) => [k, Array.isArray(v) ? v.join(" ") : String(v)])
          )
        : {};

      let errorMessage;
      if (status === 403) {
        errorMessage = serverMsg || "You don't have permission to save this project";
      } else if (status === 500) {
        errorMessage = "Server error occurred while saving project";
      } else if (serverMsg) {
        errorMessage = serverMsg;
      } else {
        errorMessage = `Save failed (${status || "network error"})`;
      }

      toast.error(errorMessage, { duration: 6000, style: { whiteSpace: "pre-wrap" } });
      setState((prev) => ({
        ...prev,
        apiError: errorMessage,
        errors: { ...prev.errors, ...serverErrors },
        saving: false,
      }));
    }
  }, [onSave, onSuccess, draftKey]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (hasErrors) {
      setState((s) => ({ ...s, errors, apiError: "Please fix the highlighted fields." }));
      return;
    }
    save();
  };

  // ---- Cmd/Ctrl + S (ref so CodeMirror keybinding always calls latest) ----
  const saveRef = useRef(save);
  const hasErrorsRef = useRef(hasErrors);
  useEffect(() => { saveRef.current = save; }, [save]);
  useEffect(() => { hasErrorsRef.current = hasErrors; }, [hasErrors]);

  const cmExtensions = useMemo(() => [
    ...cmBaseExtensions,
    keymap.of([{
      key: "Mod-s",
      run: () => {
        if (!hasErrorsRef.current) saveRef.current();
        else setState((s) => ({ ...s, apiError: "Please fix the highlighted fields." }));
        return true;
      },
    }]),
  ], []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!hasErrorsRef.current) saveRef.current();
        else setState((s) => ({ ...s, apiError: "Please fix the highlighted fields." }));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ---- Drag to resize (desktop only) ----
  useEffect(() => {
    if (isMobile) return;
    const onMove = (e) => {
      if (!dragging.current) return;
      const container = document.getElementById("pc-container");
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.min(maxSplitPct, Math.max(minSplitPct, (x / rect.width) * 100));
      setSplit(pct);
    };
    const stop = () => (dragging.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", stop);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", stop);
    };
  }, [isMobile, minSplitPct, maxSplitPct]);

  const fieldClass = (key) => (errors[key] ? "pc-field pc-error" : "pc-field");
  const errText = (key) => errors[key] && <div className="pc-error-text">{errors[key]}</div>;
  const gridTemplate = isMobile ? "1fr" : `${split}% 6px ${100 - split}%`;

  const handleCancel = () => {
    const s = stateRef.current;
    const hasContent = s.title || s.subtitle || s.body || s.technology || s.skill;
    if (hasContent && !window.confirm("You have unsaved changes. Leave anyway?")) return;
    onCancel ? onCancel() : (window.location = "/");
  };

  const handleJsonImport = () => {
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch {
      toast.error("Invalid JSON");
      return;
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      toast.error("Invalid JSON — expected an object");
      return;
    }
    const knownKeys = ["title", "subtitle", "technology", "skill", "body", "publish"];
    for (const key of knownKeys) {
      if (key in parsed) setField(key, parsed[key]);
    }
    setShowJsonImport(false);
    setJsonText("");
    toast.success("Project imported");
  };

  const aiPrompt = `Write a project write-up for INERTIA7. Return a single JSON object with these fields:

{
  "title": "...",       // ≤ 150 chars. Punchy, concise — renders as H1
  "subtitle": "...",    // ≤ 150 chars. One-liner summary under the title
  "technology": "...",  // ≤ 150 chars. Primary tech (e.g. "PyTorch", "React + D3")
  "skill": "...",       // ≤ 150 chars. Discipline (e.g. "ml engineering", "data viz")
  "body": "..."         // Markdown body — see rules below
}

Body formatting rules:
- Use bullet points instead of tables wherever possible
- Fenced code blocks: use 5 backticks (\`\`\`\`\`) as the fence when the code inside contains triple backticks
- Supported syntax-highlighted languages: python, javascript, bash, json, sql, markdown
- LaTeX math: inline $...$ and block $$...$$
- Do NOT use HTML — use pure Markdown
- Structure with clear headings (## for sections)
- Use bold for key terms on first mention`;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => toast.success("Copied to clipboard"),
      () => toast.error("Failed to copy")
    );
  };

  return (
    <div id="pc-container" className="pc-container">
      <div className="pc-split" style={{ gridTemplateColumns: gridTemplate }}>
        {/* LEFT: Editor */}
        <form className="pc-pane pc-form" onSubmit={handleSubmit} noValidate style={{ overflow: "hidden" }}>
          <div className="pc-meta-header" style={{ flexShrink: 0, padding: "10px 12px 8px", gap: 6 }}>
            {state.apiError && (
              <div className="pc-alert" role="alert" style={{ marginBottom: 6, whiteSpace: "pre-wrap" }}>
                {state.apiError}
              </div>
            )}
            <div className={fieldClass("title")}>
              <label htmlFor="title" style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>Title</label>
              <input
                id="title"
                value={state.title}
                onChange={handleChange}
                placeholder="Punchy, concise title"
                style={{ ...inputStyle, padding: "6px 8px" }}
              />
              {errText("title")}
            </div>

            <div className={fieldClass("subtitle")}>
              <label htmlFor="subtitle" style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>Subtitle</label>
              <input
                id="subtitle"
                value={state.subtitle}
                onChange={handleChange}
                placeholder="Optional one-liner"
                style={{ ...inputStyle, padding: "6px 8px" }}
              />
              {errText("subtitle")}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <div className={fieldClass("technology")} style={{ flex: 1 }}>
                <label htmlFor="technology" style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>Technology</label>
                <input
                  id="technology"
                  value={state.technology}
                  onChange={handleChange}
                  placeholder="e.g. PyTorch"
                  style={{ ...inputStyle, padding: "6px 8px" }}
                />
                {errText("technology")}
              </div>

              <div className={fieldClass("skill")} style={{ flex: 1 }}>
                <label htmlFor="skill" style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>Skill</label>
                <input
                  id="skill"
                  value={state.skill}
                  onChange={handleChange}
                  placeholder="e.g. ml engineering"
                  style={{ ...inputStyle, padding: "6px 8px" }}
                />
                {errText("skill")}
              </div>
            </div>
          </div>

          <div style={{ flexShrink: 0, padding: "8px 12px 2px" }}>
            <label style={{ fontSize: 11, color: "#64748b" }}>Body <span style={{ color: "#475569", fontWeight: 400 }}>— Markdown · LaTeX</span></label>
          </div>
          <div style={{
            flex: "1 1 0", minHeight: 0, overflow: "hidden",
            margin: "0 12px 8px", border: "1px solid #334155", borderRadius: 8,
          }}>
            <CodeMirror
              value={state.body}
              onChange={(val) => setField("body", val)}
              theme={activeTheme}
              extensions={cmExtensions}
              placeholder="Start writing..."
              height="100%"
              style={{ height: "100%", fontSize: 14 }}
              basicSetup={{
                lineNumbers: false,
                foldGutter: false,
                highlightActiveLine: true,
                highlightSelectionMatches: true,
                bracketMatching: true,
                closeBrackets: true,
                autocompletion: false,
                crosshairCursor: false,
                rectangularSelection: false,
                indentOnInput: true,
              }}
            />
          </div>

          <div className="pc-toolbar">
            <div className="pc-toolbar-left">
              {showPublishToggle && (
                <label className="pc-toggle">
                  <Toggle checked={state.publish} onChange={togglePublish} />
                  <span className="pc-status">{state.publish ? "Save as public" : "Save as private"}</span>
                </label>
              )}
              <button
                type="button"
                onClick={cycleTheme}
                title={`Theme: ${EDITOR_THEMES[editorThemeKey]?.label} (click to cycle)`}
                style={{
                  width: 22, height: 22, borderRadius: "50%", marginLeft: 8,
                  background: EDITOR_THEMES[editorThemeKey]?.swatch || "#000",
                  border: "2px solid #475569", cursor: "pointer", padding: 0,
                  transition: "transform 150ms ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.15)")}
                onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              />
              <button
                type="button"
                onClick={() => setShowContributors(true)}
                title="Manage contributors"
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  background: "none", border: "1px solid #334155", borderRadius: 6,
                  color: "#94a3b8", fontSize: 13, padding: "4px 10px",
                  cursor: "pointer", marginLeft: 4,
                }}
              >
                <PeopleIcon style={{ fontSize: 16 }} />
                <span>{contributors.length}</span>
              </button>
              {projectId && (
                <div style={{ position: "relative", marginLeft: 4 }}>
                  <button
                    type="button"
                    onClick={() => setShowBannerUpload((v) => !v)}
                    title={state.picture_link ? "Change banner image" : "Add banner image"}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: state.picture_link ? "#59c6c122" : "none",
                      border: state.picture_link ? "1px solid #59c6c1" : "1px solid #334155",
                      borderRadius: 6,
                      color: state.picture_link ? "#59c6c1" : "#94a3b8",
                      fontSize: 13, padding: "4px 10px", cursor: "pointer",
                    }}
                  >
                    <ImageIcon style={{ fontSize: 16 }} />
                    <span>{state.picture_link ? "Banner" : "Banner"}</span>
                  </button>
                  {showBannerUpload && (
                    <div style={{
                      position: "absolute", bottom: "calc(100% + 8px)", left: 0,
                      background: "#1e293b", border: "1px solid #334155", borderRadius: 8,
                      padding: 12, zIndex: 100, width: 260,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                    }}>
                      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8 }}>Project Banner Image</div>
                      <ImageUpload
                        type="project-banner"
                        projectId={projectId}
                        currentUrl={state.picture_link || ""}
                        token={localStorage.getItem("TOKEN")}
                        onUpload={(url) => {
                          setState((s) => ({ ...s, picture_link: url }));
                          setShowBannerUpload(false);
                          if (onSave) {
                            onSave({ picture_link: url }).catch(() => {
                              toast.error("Failed to save banner");
                            });
                          }
                        }}
                        style={{ height: 100, borderRadius: 6 }}
                      />
                      {state.picture_link && (
                        <button
                          type="button"
                          onClick={() => {
                            setState((s) => ({ ...s, picture_link: "" }));
                            setShowBannerUpload(false);
                            if (onSave) {
                              onSave({ picture_link: "" }).catch(() => {
                                toast.error("Failed to remove banner");
                              });
                            }
                          }}
                          style={{
                            marginTop: 8, fontSize: 11, color: "#ef4444",
                            background: "none", border: "none", cursor: "pointer",
                            padding: 0,
                          }}
                        >
                          Remove banner
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="pc-toolbar-right">
              {onDelete && (
                <button
                  className="btn btn-outline-secondary btn-sm"
                  type="button"
                  onClick={onDelete}
                  title="Delete project"
                  style={{ color: "#ef4444", borderColor: "#7f1d1d" }}
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowGuide(true)}
                title="Content Guide"
                style={{
                  background: "none", border: "1px solid #334155", borderRadius: "50%",
                  color: "#94a3b8", fontSize: 11, width: 24, height: 24,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", padding: 0,
                }}
              >
                ?
              </button>
              <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => setShowJsonImport(true)} title="Import JSON">
                Import
              </button>
              <button className="btn btn-outline-secondary btn-sm" type="button" onClick={clearDraftAndRefresh} title="Clear local draft">
                Clear
              </button>
              <button
                className="btn btn-dark btn-sm"
                type="button"
                onClick={handleCancel}
                style={{ background: "#fff", color: "#334155", borderColor: "#e2e8f0" }}
              >
                Cancel
              </button>
              <button
                className="btn btn-dark btn-sm"
                type="submit"
                disabled={state.saving}
                style={{ background: "#59c6c1", borderColor: "#59c6c1", color: "#0f172a" }}
              >
                {state.saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </form>

        {/* Gutter / Preview only on desktop */}
        {!isMobile && <div className="pc-gutter" onMouseDown={() => (dragging.current = true)} title="Drag to resize" />}
        {!isMobile && (
          <div className="pc-pane pc-preview" style={{ padding: "16px 48px", overflowX: "hidden", position: "relative" }}>
            {projectId && (
              <div style={{ position: "sticky", top: 8, zIndex: 10, display: "flex", justifyContent: "flex-end", marginBottom: -40, pointerEvents: "none" }}>
                <a
                  href={`/projects/${projectId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View live project"
                  title="View live project"
                  style={{
                    pointerEvents: "auto",
                    width: 36, height: 36, borderRadius: "50%",
                    backgroundColor: "#fff", display: "flex",
                    alignItems: "center", justifyContent: "center",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    border: "1px solid var(--border)",
                    textDecoration: "none",
                    transition: "background-color 200ms ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#59c6c1")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                >
                  <VisibilityIcon style={{ color: "#333", fontSize: "1.1rem" }} />
                </a>
              </div>
            )}
            <ProjectDetailContent
              project={{
                title: state.title,
                subtitle: state.subtitle,
                technology: state.technology,
                skill: state.skill,
                body: state.body,
                publish: state.publish,
                owner: contributors,
              }}
              showActions={false}
              isDarkMode={isDarkMode}
            />
          </div>
        )}
      </div>

      {showJsonImport && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setShowJsonImport(false)}
        >
          <div
            style={{
              background: "#1e293b", borderRadius: 8, padding: 24,
              width: "90%", maxWidth: 520, color: "#e2e8f0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 style={{ margin: "0 0 12px", color: "#59c6c1" }}>Import JSON</h4>
            <p style={{ margin: "0 0 12px", fontSize: 13, color: "#94a3b8" }}>
              Paste a JSON object with any of: title, subtitle, technology, skill, body, publish
            </p>
            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              rows={10}
              style={{
                width: "100%", fontFamily: "monospace", fontSize: 13,
                background: "#000000e6", color: "#59c6c1", border: "1px solid #334155",
                borderRadius: 4, padding: 10, resize: "vertical",
              }}
              placeholder={'{\n  "title": "...",\n  "subtitle": "...",\n  "technology": "...",\n  "skill": "...",\n  "body": "..."\n}'}
              autoFocus
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                onClick={() => { setShowJsonImport(false); setJsonText(""); }}
              >
                Cancel
              </button>
              <button
                className="btn btn-dark btn-sm"
                type="button"
                onClick={handleJsonImport}
                style={{ background: "#59c6c1", borderColor: "#59c6c1", color: "#0f172a" }}
              >
                Import
              </button>
            </div>
          </div>
        </div>
      )}

      {showContributors && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => { setShowContributors(false); setContribSearch(""); setContribResults([]); }}
        >
          <div
            style={{
              background: "#1e293b", borderRadius: 12, padding: 0,
              width: "90%", maxWidth: 440, color: "#e2e8f0",
              boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: "20px 24px 16px", borderBottom: "1px solid #334155",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <div>
                <h4 style={{ margin: 0, color: "#e2e8f0", fontSize: 16, fontWeight: 600 }}>Contributors</h4>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: "#64748b" }}>
                  Add people who worked on this project
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setShowContributors(false); setContribSearch(""); setContribResults([]); }}
                style={{
                  background: "none", border: "none", color: "#64748b",
                  cursor: "pointer", padding: 4, display: "flex",
                }}
              >
                <CloseIcon style={{ fontSize: 20 }} />
              </button>
            </div>

            {/* Search */}
            <div style={{ padding: "16px 24px 12px" }}>
              <input
                value={contribSearch}
                onChange={(e) => {
                  setContribSearch(e.target.value);
                  searchUsers(e.target.value);
                }}
                placeholder="Search by name or username..."
                autoFocus
                style={{
                  ...inputStyle, width: "100%", fontSize: 14,
                  padding: "10px 14px", borderRadius: 8,
                  border: "1px solid #334155",
                }}
              />
              {contribResults.length > 0 && (
                <div style={{
                  background: "#0f172a", border: "1px solid #334155", borderRadius: 8,
                  marginTop: 8, maxHeight: 180, overflowY: "auto",
                }}>
                  {contribResults.map((u) => (
                    <div
                      key={u.id || u.pk}
                      onClick={() => addContributor(u)}
                      style={{
                        padding: "10px 14px", cursor: "pointer", fontSize: 14,
                        color: "#e2e8f0", borderBottom: "1px solid #1e293b",
                        display: "flex", alignItems: "center", gap: 10,
                        transition: "background 100ms ease",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#1e293b")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <div style={{
                        width: 32, height: 32, borderRadius: "50%",
                        background: "#334155", display: "flex",
                        alignItems: "center", justifyContent: "center",
                        fontSize: 13, fontWeight: 600, color: "#94a3b8",
                        flexShrink: 0,
                      }}>
                        {(u.first_name?.[0] || u.username?.[0] || "?").toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500 }}>{u.username}</div>
                        {(u.first_name || u.last_name) && (
                          <div style={{ fontSize: 12, color: "#64748b" }}>
                            {u.first_name} {u.last_name}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {contribSearching && (
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 8, textAlign: "center" }}>Searching...</div>
              )}
            </div>

            {/* Current contributors list */}
            <div style={{ padding: "0 24px 20px" }}>
              {contributors.length === 0 ? (
                <div style={{ textAlign: "center", padding: "20px 0", color: "#475569", fontSize: 13 }}>
                  No contributors yet
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {contributors.map((c) => (
                    <div
                      key={c.id || c.pk}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "8px 10px", borderRadius: 8,
                        transition: "background 100ms ease",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#0f172a")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: "50%",
                          background: "#334155", display: "flex",
                          alignItems: "center", justifyContent: "center",
                          fontSize: 14, fontWeight: 600, color: "#59c6c1",
                          flexShrink: 0,
                        }}>
                          {(c.first_name?.[0] || c.username?.[0] || "?").toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 14 }}>
                            {c.first_name || c.last_name
                              ? `${c.first_name || ""} ${c.last_name || ""}`.trim()
                              : c.username}
                          </div>
                          {(c.first_name || c.last_name) && (
                            <div style={{ fontSize: 12, color: "#64748b" }}>@{c.username}</div>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeContributor(c.id || c.pk)}
                        title="Remove contributor"
                        style={{
                          background: "none", border: "none", color: "#475569",
                          cursor: "pointer", padding: 4, display: "flex",
                          borderRadius: 4, transition: "color 100ms ease",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "#ef4444")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "#475569")}
                      >
                        <CloseIcon style={{ fontSize: 18 }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showGuide && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setShowGuide(false)}
        >
          <div
            style={{
              background: "#1e293b", borderRadius: 8, padding: 24,
              width: "90%", maxWidth: 600, maxHeight: "85vh", overflowY: "auto",
              color: "#e2e8f0",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 style={{ margin: "0 0 16px", color: "#59c6c1" }}>Content Guide</h4>

            <div style={{ fontSize: 13, lineHeight: 1.6 }}>
              <h5 style={{ color: "#e2e8f0", margin: "0 0 8px" }}>Field Limits</h5>
              <ul style={{ margin: "0 0 16px", paddingLeft: 20 }}>
                <li><strong>Title</strong> — required, max 150 chars. Renders as H1.</li>
                <li><strong>Subtitle</strong> — optional, max 150 chars. One-liner under the title.</li>
                <li><strong>Technology</strong> — optional, max 150 chars (e.g. "PyTorch", "React + D3").</li>
                <li><strong>Skill</strong> — optional, max 150 chars (e.g. "ml engineering").</li>
                <li><strong>Body</strong> — required. Markdown.</li>
              </ul>

              <h5 style={{ color: "#e2e8f0", margin: "0 0 8px" }}>Formatting Best Practices</h5>
              <ul style={{ margin: "0 0 16px", paddingLeft: 20 }}>
                <li>Use <strong>bullet points instead of tables</strong> — tables scroll horizontally and can be hard to read.</li>
                <li>Code blocks: use <strong>5 backticks</strong> (<code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>`````</code>) as the fence when code inside contains triple backticks.</li>
                <li>Supported highlighted languages: <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>python</code>, <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>javascript</code>, <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>bash</code>, <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>json</code>, <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>sql</code>, <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>markdown</code>.</li>
                <li>LaTeX math: inline <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>$...$</code> and block <code style={{ background: "#334155", padding: "1px 4px", borderRadius: 3 }}>$$...$$</code>.</li>
                <li>Structure with <strong>## headings</strong> for sections.</li>
                <li>Bold key terms on first mention.</li>
                <li>Stick to pure Markdown — avoid raw HTML.</li>
              </ul>

              <h5 style={{ color: "#e2e8f0", margin: "0 0 8px" }}>AI Prompt</h5>
              <p style={{ color: "#94a3b8", margin: "0 0 8px" }}>
                Copy this prompt and paste it into Claude or ChatGPT. It will return a JSON object you can import directly with "Import JSON".
              </p>
              <pre
                onClick={() => copyToClipboard(aiPrompt)}
                style={{
                  background: "#000000e6", color: "#59c6c1", border: "1px solid #334155",
                  borderRadius: 4, padding: 12, fontSize: 12, whiteSpace: "pre-wrap",
                  wordBreak: "break-word", maxHeight: 240, overflowY: "auto",
                  cursor: "pointer", transition: "border-color 150ms ease",
                }}
                title="Click to copy"
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#59c6c1")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#334155")}
              >{aiPrompt}</pre>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                onClick={() => setShowGuide(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
